import { zodResolver } from '@hookform/resolvers/zod';
import { createId } from '@paralleldrive/cuid2';
import { Trash2Icon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import {
  SkuSelectionMethod,
  SkuType,
  type SkuAssemblyComponentDTO,
} from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { Input } from '@/components/core/input';
import { SelectItem } from '@/components/core/select';
import { Separator } from '@/components/core/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/core/tabs';
import { useDialogContext } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { NumberFormField } from '@/components/forms/number-form-field';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

import { SkuBadge } from '../../components/sku-badge';
import { SkuCombobox } from '../../components/sku-combobox';
import { getSelectionMethodsLabel, selectionMethodOptions } from './selection-methods';
import {
  getMysterySkuComponents,
  isMysteryGroupComponent,
  type MysteryComponent,
  type MysterySkuComponent,
} from './use-mystery-components';

type ComponentType = 'group' | 'fixed';

const schema = z
  .object({
    mode: z.enum(['group', 'fixed']),
    groupName: z.string().trim().optional(),
    groupQuantity: z.number().optional(),
    selectionMethodLabel: z.string().trim().optional(),
  })
  .superRefine((values, context) => {
    if (values.mode !== 'group') return;

    if (!values.groupName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Group name is required',
        path: ['groupName'],
      });
    }

    if (!values.groupQuantity) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Group selection quantity must be at least 1',
        path: ['groupQuantity'],
      });
    }

    if (!values.selectionMethodLabel) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Preferred method of selecting options is required',
        path: ['selectionMethodLabel'],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

type EditableMysteryComponent = MysterySkuComponent & { id: string };

type Props = {
  existingMysteryComponent?: MysteryComponent;
  allAssemblyComponents: SkuAssemblyComponentDTO[];
  onSubmit: (component: MysteryComponent) => Promise<Result> | Promise<void> | void;
};

const DEFAULT_METHODS = [
  SkuSelectionMethod.MOST_UNIQUE_TO_ORDER,
  SkuSelectionMethod.MOST_AVAILABLE,
] as SkuSelectionMethod[];

const createDefaultComponent = (): EditableMysteryComponent => ({
  id: createId(),
  quantity: 1,
});

export function MysteryComponentManagementDialog({
  existingMysteryComponent,
  allAssemblyComponents,
  onSubmit,
}: Props) {
  const { isOpen } = useDialogContext();
  const [rows, setRows] = useState<EditableMysteryComponent[]>([createDefaultComponent()]);

  // TODO: shouldn't be necessary once using Base UI for dialogs
  const portalContainerRef = useRef<HTMLDivElement>(null);

  const defaultValues = useMemo<FormValues>(() => {
    const isGrouped =
      !existingMysteryComponent || isMysteryGroupComponent(existingMysteryComponent);
    return {
      mode: isGrouped ? 'group' : 'fixed',
      groupName: isGrouped && existingMysteryComponent ? (existingMysteryComponent.name ?? '') : '',
      groupQuantity:
        isGrouped && existingMysteryComponent ? (existingMysteryComponent.quantity ?? 1) : 1,
      selectionMethodLabel:
        isGrouped && existingMysteryComponent
          ? getSelectionMethodsLabel(existingMysteryComponent.selectionMethods)
          : getSelectionMethodsLabel(DEFAULT_METHODS),
    };
  }, [existingMysteryComponent]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const mode = form.watch('mode');

  useEffect(() => {
    if (!isOpen) return;
    const existingRows = getMysterySkuComponents(existingMysteryComponent).map((component) => ({
      ...component,
      id: component.id ?? createId(),
    }));
    setRows(existingRows.length > 0 ? existingRows : [createDefaultComponent()]);
    form.reset(defaultValues);
  }, [existingMysteryComponent, form, isOpen, defaultValues]);

  useEffect(() => {
    if (mode === 'fixed') {
      setRows((current) => (current.length > 1 ? [current[0]] : current));
    } else if (mode === 'group') {
      setRows((current) => (!current.length ? [createDefaultComponent()] : current));
    }
  }, [mode]);

  const ownedComponentIds = useMemo(
    () =>
      new Set(getMysterySkuComponents(existingMysteryComponent).map((component) => component.id)),
    [existingMysteryComponent],
  );

  const excludeSkuIds = useMemo(
    () =>
      [
        ...allAssemblyComponents.filter((component) => !ownedComponentIds.has(component.id)),
        ...rows,
      ]
        .map((component) => component.componentSku?.id ?? component.componentSkuId)
        .filter((value): value is string => Boolean(value)),
    [allAssemblyComponents, ownedComponentIds, rows],
  );

  const currentLabel = form.watch('selectionMethodLabel');
  const hasCustomSelectionMethod = useMemo(
    () =>
      currentLabel &&
      currentLabel.length > 0 &&
      !selectionMethodOptions.some((option) => option.label === currentLabel),
    [currentLabel],
  );

  const canSave = useMemo(() => {
    const hasValidRows =
      rows.length > 0 &&
      rows.every((row) => row.componentSkuId && row.quantity && row.quantity >= 1);

    if (!hasValidRows) return false;
    if (mode === 'fixed') return true;

    const hasErrors = Object.keys(form.formState.errors).length > 0;
    return !hasErrors;
  }, [form, mode, rows]);

  return (
    <FormDialogContent
      className="sm:max-w-3xl"
      form={form}
      submitLabel="Save"
      isSubmitDisabled={!canSave}
      onSubmit={(values) => {
        if (values.mode === 'fixed') {
          return onSubmit(rows[0]);
        }

        const selectedOption = selectionMethodOptions.find(
          (option) => option.label === values.selectionMethodLabel,
        );

        return onSubmit({
          id:
            existingMysteryComponent && isMysteryGroupComponent(existingMysteryComponent)
              ? existingMysteryComponent.id
              : '',
          name: values.groupName,
          quantity: values.groupQuantity,
          selectionMethods: selectedOption?.value ?? DEFAULT_METHODS,
          components: rows,
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>{existingMysteryComponent ? 'Manage component' : 'Add component'}</DialogTitle>
        <Separator />
        <Tabs
          className="w-full"
          value={mode}
          onValueChange={(value) =>
            form.setValue('mode', value as ComponentType, { shouldDirty: true })
          }
        >
          <TabsList className="w-full">
            <TabsTrigger value="group">Mystery Group</TabsTrigger>
            <TabsTrigger value="fixed">Fixed SKU</TabsTrigger>
          </TabsList>
        </Tabs>
        <DialogDescription>
          {mode === 'fixed'
            ? 'Add the fixed SKU and the required quantity to configure this component.'
            : 'Add the option SKUs and the required quantity to configure this mystery group.'}
        </DialogDescription>
      </DialogHeader>
      <div ref={portalContainerRef} className="absolute top-0 left-0 h-0 w-0 overflow-visible" />

      <DialogBody>
        <div className="space-y-4">
          {mode === 'group' ? (
            <div className="space-y-4">
              <TextFormField
                control={form.control}
                name="groupName"
                label="Group name"
                placeholder="Enter group name"
              />
              <NumberFormField
                control={form.control}
                name="groupQuantity"
                label="Group selection quantity"
                min={1}
              />
              <SelectFormField
                control={form.control}
                name="selectionMethodLabel"
                label="Preferred method of selecting options"
                className="w-full"
              >
                {selectionMethodOptions.map((option) => (
                  <SelectItem key={option.label} value={option.label}>
                    {option.label}
                  </SelectItem>
                ))}
                {hasCustomSelectionMethod ? (
                  <SelectItem value={form.getValues('selectionMethodLabel') ?? ''}>
                    {form.getValues('selectionMethodLabel')}
                  </SelectItem>
                ) : null}
              </SelectFormField>
            </div>
          ) : null}

          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_140px_auto] gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
              <div>SKU / Description</div>
              <div className="text-right">{mode === 'fixed' ? 'Quantity' : 'Qty per Option'}</div>
              <div />
            </div>
            <div className="space-y-2">
              {rows.map((row, index) => (
                <div key={row.id} className="grid grid-cols-[1fr_140px_auto] gap-2">
                  {row.componentSku ? (
                    <div className="flex min-h-8 min-w-0 items-center gap-2 rounded-md border px-3 text-sm">
                      <SkuBadge
                        name={row.componentSku.name ?? 'Unknown SKU'}
                        type={row.componentSku.type}
                      />
                      {row.componentSku.description ? (
                        <span className="truncate text-sm">{row.componentSku.description}</span>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-7"
                        onClick={() =>
                          setRows((currentRows) =>
                            currentRows.map((candidate) =>
                              candidate.id === row.id
                                ? {
                                    ...candidate,
                                    componentSku: undefined,
                                    componentSkuId: undefined,
                                  }
                                : candidate,
                            ),
                          )
                        }
                      >
                        Edit
                      </Button>
                    </div>
                  ) : (
                    <SkuCombobox
                      queryParams={{
                        type: SkuType.STANDARD,
                        excludeSkuIds: excludeSkuIds.filter((id) => id !== row.componentSkuId),
                      }}
                      portalContainerRef={portalContainerRef}
                      onSelect={(componentSku) =>
                        setRows((currentRows) =>
                          currentRows.map((candidate) =>
                            candidate.id === row.id
                              ? {
                                  ...candidate,
                                  componentSku,
                                  componentSkuId: componentSku.id,
                                }
                              : candidate,
                          ),
                        )
                      }
                    />
                  )}

                  <Input
                    type="number"
                    min={1}
                    step={1}
                    aria-label={mode === 'fixed' ? 'Quantity' : `Quantity for option ${index + 1}`}
                    value={Number.isFinite(row.quantity ?? 0) ? row.quantity : ''}
                    onChange={(event) =>
                      setRows((currentRows) =>
                        currentRows.map((candidate) =>
                          candidate.id === row.id
                            ? {
                                ...candidate,
                                quantity:
                                  event.currentTarget.value === ''
                                    ? 0
                                    : event.currentTarget.valueAsNumber,
                              }
                            : candidate,
                        ),
                      )
                    }
                  />

                  {mode === 'group' ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={`Delete option ${index + 1}`}
                      onClick={() =>
                        setRows((currentRows) =>
                          currentRows.length > 1
                            ? currentRows.filter((candidate) => candidate.id !== row.id)
                            : currentRows,
                        )
                      }
                      disabled={rows.length === 1}
                    >
                      <Trash2Icon />
                    </Button>
                  ) : (
                    <div />
                  )}
                </div>
              ))}
            </div>
          </div>

          {mode === 'group' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setRows((currentRows) => [...currentRows, createDefaultComponent()])}
            >
              Add option
            </Button>
          ) : null}
        </div>
      </DialogBody>
    </FormDialogContent>
  );
}
